import { createLogger } from '@utils/logger';
const logger = createLogger({ prefix: 'AnchorScroll' });
const HEADER_OFFSET = 150;
const interceptors = [];
export function registerScrollInterceptor(interceptor) {
    interceptors.push(interceptor);
    logger.info(`Interceptor registered (${interceptors.length} total).`);
}
export async function scrollToElement(element) {
    logger.info(`Scrolling to element: #${element.id || '(no id)'}, running ${interceptors.length} interceptor(s).`);
    for (const interceptor of interceptors) {
        await interceptor(element);
    }
    const top = element.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
    logger.info(`Scroll target offset: ${Math.round(top)}px (header offset: ${HEADER_OFFSET}px).`);
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}
function safeQuerySelector(selector) {
    try {
        return document.querySelector(selector);
    }
    catch {
        logger.warn(`Invalid selector: ${selector}`);
        return null;
    }
}
function resolveElement(hash) {
    const direct = safeQuerySelector(hash);
    if (direct)
        return direct;
    if (!hash.startsWith('#_')) {
        const fallback = `#_${hash.slice(1)}`;
        const legacy = safeQuerySelector(fallback);
        if (legacy) {
            logger.info(`Resolved "${hash}" via legacy fallback "${fallback}".`);
            return legacy;
        }
    }
    return null;
}
export function navigateToHash(hash) {
    const target = resolveElement(hash);
    if (!target)
        return false;
    scrollToElement(target);
    return true;
}
function handleAnchorClick(e) {
    const anchor = e.target.closest('a[href*="#"]');
    if (!anchor)
        return;
    let url;
    try {
        url = new URL(anchor.href, window.location.href);
    }
    catch {
        return;
    }
    if (url.pathname !== window.location.pathname || !url.hash)
        return;
    const target = resolveElement(url.hash);
    if (!target)
        return;
    logger.info(`Anchor click intercepted: ${url.hash}`);
    e.preventDefault();
    history.pushState(null, '', url.hash);
    scrollToElement(target);
}
function init() {
    document.addEventListener('click', handleAnchorClick);
    window.addEventListener('popstate', () => {
        if (window.location.hash) {
            logger.info(`Popstate navigation: ${window.location.hash}`);
            navigateToHash(window.location.hash);
        }
    });
    if (window.location.hash) {
        logger.info(`Initial hash detected: ${window.location.hash} - suppressing native jump.`);
        history.scrollRestoration = 'manual';
        window.scrollTo(0, 0);
        const onReady = () => {
            logger.info(`DOM ready, navigating to initial hash: ${window.location.hash}`);
            requestAnimationFrame(() => navigateToHash(window.location.hash));
        };
        if (document.readyState !== 'loading') {
            onReady();
        }
        else {
            document.addEventListener('DOMContentLoaded', onReady, { once: true });
        }
    }
    logger.info('Anchor scroll initialized.');
}
init();
